import { Component, Input, Output, EventEmitter, ViewChild, forwardRef, TemplateRef, OnChanges, ChangeDetectorRef, SimpleChanges, HostListener, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { InstanceService } from '../../services/instance.service';
import { ProjectDataService } from '../../services/project-data-service';
import { ProjectService } from '../../services/project.service';
import { AlertifyService } from '../../services/alertify.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { AuthService } from '../../services/auth.service';
import { LaunchConfirmationComponent } from '../launch-confirmation/launch-confirmation.component';
import { GoLiveChecklistComponent } from '../go-live-checklist/go-live-checklist.component';

@Component({
    selector: 'or-dropdown-status',
    templateUrl: './dropdown-status.component.html',
    styleUrls: ['./dropdown-status.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownStatusComponent),
            multi: true
        }
    ],
    standalone: false
})
export class DropdownStatusComponent implements ControlValueAccessor, OnChanges, OnDestroy {

  @Input() values = ['Test', 'Soft Launch', 'Full Launch', 'Paused', 'Closed', 'Invoiced'];
  @Input() projectId: string;
  @Input() partnerId: string;
  @Input() partnerName: string;
  @Input() status: string;
  @Input() autosave = true;
  @Input() skipPartnerUpdate = false;
  @Input() skipProjectUpdate = false;
  @Input() disabled: boolean = false;
  @Input() isPositionRelative: boolean = false;
  @Input() container: string = "body";
  @Input() isClient: boolean = false;
  @Input() surveyLink: string = '';
  @Input() showLaunchPopup: boolean = false;
  @Input() isInsideModal: boolean = false;
  @Input() showIcons: boolean = true;
  
  @Output() changed = new EventEmitter();
  @Output() updatePartners = new EventEmitter();
  @Output() updateProject = new EventEmitter();

  @ViewChild('invoiceConfirm')
  modelRefInvoiceConfirm: TemplateRef<any>;

  @ViewChild('modalNoLiveUrl')
  modalNoLiveUrl: TemplateRef<any>;

  @ViewChild('modalStatusChangeToLive')
  modalStatusChangeToLive: TemplateRef<any>;
  openModalRef: any;

  _values:any = [];
  updatePartnerStatus = false;
  updateProjectStatus = false;
  savingStatus = false;
  closed = false;
  timer;
  newStatusValue;
  statusClass = 'nav-status-test';
  isORInstance = false; // Cache auth check
  isClientUser = false; // Cache auth check
  private documentClickListener: ((event: MouseEvent) => void) | null = null; // Store listener reference


  statusDefault = [
    // for project
    { status: 'Test', class: 'nav-status-test'},
    { status: 'Soft Launch', class: 'nav-status-soft-launch'},
    { status: 'Full Launch', class: 'nav-status-full-launch'},
    { status: 'Paused', class: 'nav-status-paused'},
    { status: 'Closed', class: 'nav-status-closed'},
    { status: 'Invoiced', class: 'nav-status-invoiced'},

    // for bidding
    { status: 'In Progress',    class: 'nav-status-test' },
    { status: 'Sent to Client', class: 'nav-status-closed' },
    { status: 'Closed Won',     class: 'nav-status-full-launch' },
    { status: 'Closed Lost',    class: 'nav-status-paused' },

    // for the vendor-facing bid status
    { status: 'Open',      class: 'nav-status-test' },
    { status: 'Submitted', class: 'nav-status-full-launch' },
  ];

  @ViewChild('dropdown', {static: false}) dropdown;
  listenerFn: () => void;
  propagateChange: (_: any) => {};

  constructor(
    private modalService: BsModalService,
    private projectService: ProjectService,
    private projectDataService: ProjectDataService,
    private authService: AuthService,
    private alertify: AlertifyService,
    private cdr: ChangeDetectorRef) { }

  ngOnInit() {
    // Cache auth checks once during initialization
    this.isORInstance = this.authService.isORInstance();
    this.isClientUser = this.authService.isClient();
    // Initialize values once
    this.initializeValues();
  }

  ngOnChanges(changes: SimpleChanges) {
    // Only reinitialize values if the 'values' input actually changed
    if (changes['values'] && !changes['values'].firstChange) {
      this.initializeValues();
    }

    // Only update status class if status actually changed
    if (changes['status'] && changes['status']?.previousValue !== changes['status']?.currentValue) {
      this.toggleStatusClass();
      this.cdr.markForCheck();
    }
  }

  private initializeValues() {
    this._values = [];
    if (this.values) {
      this.values.map(item => {
        const found = this.statusDefault.find(x => x.status === item);
        this._values.push({status: found?.status, class: found?.class});
      });
    } else {
      this._values = this.statusDefault;
    }

    // Hide 'Invoiced' status if OR Instances (use cached value)
    if (this.isORInstance) {
      this._values = this._values.filter(x => x.status !== 'Invoiced');
    }
  }

  toggleStatusClass() {
    this.statusClass = this.statusDefault.find(x => x.status === this.status)?.class;
  }

  applyStatus(newValue, ignoreAutosave = false) {
    if (newValue != this.status && (this.autosave || ignoreAutosave)) {
      //when switching from Soft Launch to Soft Launch at project level, we will keep the dropdown open to allow  updating the partners
      let showDropdownForStep2 = (this.status === 'Soft Launch' &&  newValue === 'Full Launch' && this.partnerId == null);

      if (newValue === 'Invoiced') {
        this.openModalRef = this.modalService.show(this.modelRefInvoiceConfirm);
        this.cdr.markForCheck();
      }
      else if (((this.status === 'Test' || this.status === 'Paused') && (newValue === 'Soft Launch' || newValue === 'Full Launch')) && this.showLaunchPopup && this.partnerId == null) {
        this.dropdown?.hide();
        this.removeDocumentClickListener();

        // NK-315: Test -> Soft Launch, project level, gets the Go Live Checklist gate.
        // Every other project-level transition into Soft Launch/Full Launch (incl.
        // Paused -> *) keeps the existing plain confirmation modal for now.
        //
        // OpinionRoute instances only. The checklist encodes OR's own launch process - which
        // sections matter, what blocks, what merely flags - and another company's process may
        // divide those differently, so each instance gets asked before it's turned on for them
        // rather than inheriting ours. Everyone else falls through to the plain confirmation
        // modal, i.e. exactly the behaviour they have today. `isORInstance` is the established
        // instance check (auth.service.ts, covers dev/staging/live/demo), cached at init above.
        const useChecklist = this.status === 'Test' && newValue === 'Soft Launch' && this.isORInstance;
        const modalComponent: any = useChecklist ? GoLiveChecklistComponent : LaunchConfirmationComponent;
        // The checklist's row layout needs a narrower, fixed-width shell than the
        // plain confirmation modal's 1080px min-width, so it gets its own class
        // instead of resizing modal-status-change-to-live for both components.
        const modalWidthClass = useChecklist ? 'modal-go-live-checklist' : 'modal-status-change-to-live';

        this.openModalRef = this.modalService.show(modalComponent, {
          class: `nav-modal-style modal-dialog-centered ${modalWidthClass}`, ignoreBackdropClick: true,
          // NK-315: the checklist accumulates ticks and inline edits, so a stray Escape
          // would silently throw away work the user has to redo row by row. Cancel is the
          // only way out of it. The plain confirmation modal keeps Escape — it holds no
          // state worth protecting.
          keyboard: !useChecklist,
          initialState: {oldStatus: this.status, status: newValue, projectId: this.projectId, partnerId: this.partnerId, partnerName: this.partnerName}})

        this.openModalRef.content.close.subscribe((result) => {
          this.openModalRef.hide();
        });

        this.openModalRef.content.saved.subscribe((newDetails) => {
          this.openModalRef.hide();
          this.newStatusValue = newDetails.status;
          this.status = newDetails.status;
          newDetails.updateSurveyValues = true;
          this.changed.emit(newDetails);

          if (newDetails.partnerId && newDetails.applyToMore) {
            this.updateProject.emit(newDetails);
          }
          // NK-415: the checklist no longer fans out across every vendor, so it reports which
          // vendors it moved instead of setting applyToMore. Still routed through
          // updatePartners — the parent repaints vendor rows, it just has a list to honour now.
          // The applyToMore branch is kept for the plain confirmation modal, which is unchanged.
          else if (!newDetails.partnerId && (newDetails.applyToMore || newDetails.launchedPartnerIds)) {
            this.updatePartners.emit(newDetails);
          }
        });

      }
      else {
        this.newStatusValue = newValue;
        this.dropdown?.hide();
        this.removeDocumentClickListener();
        this.updateToNonLiveStatus();
      }
      
      if(showDropdownForStep2) {
         this.closed = true;
         this.dropdown.toggle(true);
      }

    }
    else if (this.autosave == false) {
      this.newStatusValue = newValue;
      this.status = newValue;
      this.changed.emit({status: this.status});
      this.dropdown?.hide();
      this.removeDocumentClickListener();
      this.toggleStatusClass();
      this.cdr.markForCheck();
    }
  }

  // updates status for Test, Paused, Invoiced and Closed
  updateToNonLiveStatus() {
    const rollback = this.status;
    this.status = this.newStatusValue;
    this.toggleStatusClass();
    this.cdr.markForCheck();

    if (!this.autosave) {
      this.changed.emit({status: this.status});
      return;
    }

    if (this.partnerId == null) {
      this.savingStatus = true;
      this.projectService.UpdateProjectStatus(this.projectId, this.status).subscribe(data => {
        this.alertify.success('Project Status updated');
        this.openModalRef?.hide();
        this.projectDataService.setProjectStatus(this.status);
        this.savingStatus = false;
        this.changed.emit({status: this.status});

        if(this.updatePartnerStatus) {
          this.updatePartnerStatusDetails();
        }

      }, error => {
        this.status = rollback;
        this.toggleStatusClass();
        this.cdr.markForCheck();
        if (error === 'Project is already Invoiced! Unable to update status!') {
          this.alertify.error(error);
          return;
        }
        if (error === 'Missing Live URL for Survey Entry Links, add to change field status') {
          this.openModalRef = this.modalService.show(this.modalNoLiveUrl, { class: 'nav-modal-style' });
        }
        else this.alertify.error('There was an issue updating this status');
      });
    }
    else {
      this.projectService.UpdateProjectPartnerStatus(this.projectId, this.partnerId, this.status).subscribe(data => {
        this.alertify.success('Vendor Status updated');
        this.changed.emit({status: this.status});
      }, error => {
        this.status = rollback;
        this.toggleStatusClass();
        this.cdr.markForCheck();
        if (error === 'Project is already Invoiced! Unable to update status!') this.alertify.error(error);
        if (error === 'Missing Live URL for Survey Entry Links, add to change field status') {
          this.openModalRef = this.modalService.show(this.modalNoLiveUrl, { class: 'nav-modal-style' });
        }
        else this.alertify.error('There was an issue updating this status');
      });

    }
  }

  openPopUp() {
    if (this.skipPartnerUpdate === false && this.status === 'Test') {
      this.closed = true;
    } else {
      this.dropdown.toggle(true);
    }
  }

  applyStatusProject(event) {
    this.dropdown.hide();
    this.removeDocumentClickListener();
    if(this.autosave) {
      if(this.savingStatus == false) {
        this.updateProjectStatusDetails();
      } else {
        this.updateProjectStatus = true;
      }
    } else {
      this.updateProject.emit({status: this.status});
    }
    event.stopPropagation();
  }

  updateProjectStatusDetails(){
    this.updateProjectStatus = false;
    this.projectService.UpdateProjectStatus(this.projectId, this.status).subscribe(data => {
      this.alertify.success('Project status updated');
      this.updateProject.emit({status: this.status});
    }, error => {
      this.alertify.error('There was an issue updating the project status');
    });
  }

  onMouseLeave(dropdown) {
    if (!this.closed) {
      this.timer = setTimeout(() => {
        dropdown?.hide();
        this.removeDocumentClickListener();
        this.cdr.markForCheck();
      }, 100);
    }
  }

  applyStatusPartners(event) {
    this.dropdown.hide();
    this.removeDocumentClickListener();
    if(this.autosave) {
      if(this.savingStatus == false) {
        this.updatePartnerStatusDetails();
      } else {
        this.updatePartnerStatus = true;
      }
    } else {
      this.updatePartners.emit({status: this.status});
    }
    event.stopPropagation();
  }

  updatePartnerStatusDetails(){
    this.updatePartnerStatus = false;
    this.projectService.UpdateAllProjectPartnerStatus(this.projectId, this.status).subscribe(data => {
      this.alertify.success('All Vendors status updated');
      this.updatePartners.emit({status: this.status});
    }, error => {
      this.alertify.error('There was an issue updating the vendors');
    });
  }

  writeValue(value: string): void {
    this.status = value ? value : '';
  }
  registerOnChange(fn: any): void {
    this.listenerFn = fn;
  }
  registerOnTouched(fn: any): void {

  }

  private onDocumentClick = (event: MouseEvent) => {
    if (this.closed) {
      const target = event.target as HTMLElement;

      // Use Element.closest() instead of while loop - much faster
      const clickableElement = target.closest('.clickable');

      if (!clickableElement) {
        // Clicked outside dropdown, hide it and remove listener
        this.closed = false;
        this.dropdown.hide();
        this.removeDocumentClickListener();
        this.cdr.markForCheck();
      }
    }
  }

  private removeDocumentClickListener() {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = null;
    }
  }

  toggleStatus(event) {
    const wasOpen = this.dropdown.isOpen;

    this.dropdown.toggle(true);
    this.closed = false;

    // If opening dropdown, attach listener. If closing, remove it.
    if (!wasOpen && this.dropdown.isOpen) {
      // Opening - attach listener
      if (!this.documentClickListener) {
        this.documentClickListener = this.onDocumentClick;
        // Use capture phase and passive for better performance
        document.addEventListener('click', this.documentClickListener, { capture: true });
      }
    } else if (wasOpen) {
      // Closing - remove listener
      this.removeDocumentClickListener();
    }

    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

  ngOnDestroy() {
    // Clean up document click listener
    this.removeDocumentClickListener();
    if (this.timer) {
      clearTimeout(this.timer);
    }
  }

}
